import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { prescriptionService } from '@/services/prescriptionService';
import { useClinic } from './ClinicContext';
import { useAuth } from './AuthContext';

interface PendingPrescriptionContextType {
  pendingCount: number;
  isLoading: boolean;
  refreshCount: () => Promise<void>;
}

const PendingPrescriptionContext = createContext<PendingPrescriptionContextType | undefined>(undefined);

export const usePendingPrescription = () => {
  const context = useContext(PendingPrescriptionContext);
  if (context === undefined) {
    throw new Error('usePendingPrescription must be used within a PendingPrescriptionProvider');
  }
  return context;
};

interface PendingPrescriptionProviderProps {
  children: ReactNode;
}

export const PendingPrescriptionProvider: React.FC<PendingPrescriptionProviderProps> = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { isPractitioner,clinic } = useClinic();
  const { user } = useAuth();

  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!isPractitioner || !clinic?.id) {
        setPendingCount(0);
        return;
      }

      try {
        setIsLoading(true);
        const response = await prescriptionService.getPendingPrescriptionCount(clinic.id);
        if (response.data?.count !== undefined) {
          setPendingCount(response.data.count);
        }
      } catch (error) {
        console.error('Error fetching pending prescription count:', error);
        setPendingCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPendingCount();
  }, [isPractitioner, user, clinic?.id]);

  useEffect(() => {
    const handleFocus = async () => {
      if (!isPractitioner || !clinic?.id) return;

      try {
        const response = await prescriptionService.getPendingPrescriptionCount(clinic.id);
        if (response.data?.count !== undefined) {
          setPendingCount(response.data.count);
        }
      } catch (error) {
        console.error('Error fetching pending prescription count:', error);
        setPendingCount(0);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isPractitioner, clinic?.id]);

  const refreshCount = async () => {
    if (!isPractitioner) {
      setPendingCount(0);
      return;
    }

    try {
      setIsLoading(true);
      const response = await prescriptionService.getPendingPrescriptionCount(clinic?.id || '');
      if (response.data?.count !== undefined) {
        setPendingCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching pending prescription count:', error);
      setPendingCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PendingPrescriptionContext.Provider
      value={{
        pendingCount,
        isLoading,
        refreshCount,
      }}
    >
      {children}
    </PendingPrescriptionContext.Provider>
  );
};

